import { QueryClient, QueryKey } from '@tanstack/react-query'

/**
 * Utility types to reflect on the provided generic types for `TQueryKeyParams` and `TCacheData`.
 * Useful for referring to a helper's types in a consistent manner throughout the codebase.
 */
export type QueryHelperTypes<TQueryKeyParams, TCacheData> = {
  /**
   * Returns the type inferred from `TCacheData`
   */
  CacheData: TCacheData

  /**
   * Returns the type inferred from `TQueryKeyParams`
   */
  QueryKeyParams: TQueryKeyParams
}

/**
 * Interface type for the Query Helper object. Defines the set of utility functions
 * available to the user that will have baked-in type information.
 */
interface QueryHelper<TQueryKeyParams, TCacheData> {
  /**
   * Function to retrieve the currently cached data for this query based
   * on the query key parameters.
   */
  getQueryData: (
    queryClient: QueryClient,
    queryKeyParams: TQueryKeyParams
  ) => TCacheData | undefined

  /**
   * Function to invalidate one specific query cache based on the query key paramters.
   */
  invalidateQuery: (
    queryClient: QueryClient,
    queryKeyParams: TQueryKeyParams
  ) => void

  /**
   * Function to invalidate all possible query caches for this query.
   */
  invalidateRootQuery: (queryClient: QueryClient) => void

  /**
   * Function to make the possible QueryKeys for this query based on the provided `QueryKeyParams`.
   */
  makeQueryKey: (queryKeyParams: TQueryKeyParams) => QueryKey

  /**
   * Gets the root of the query key, without the queryKeyParameters segment applied.
   */
  rootQueryKey: () => QueryKey

  /**
   * Function to modify the current query cache based on the query key parameters,
   * most frequently used for optimistic updates of the UI while async mutations are
   * in flight.
   * Accepts an `updaterFn` as the last argument that will perform the updates.
   * @note - The `updaterFn` callback should use an immer `produce` function internally
   * to be consistent with current patterns.
   */
  setQueryData: (
    queryClient: QueryClient,
    queryKeyParams: TQueryKeyParams,
    updaterFn: (arg: TCacheData | undefined) => TCacheData | undefined
  ) => TCacheData | undefined
}

/**
 * Function to create a "query helper" for a given query. This helper object will provide
 * a set of utility functions with baked-in type information for the Query Key and Query Cache
 * to make several operations easier.
 */
export const makeQueryHelper = <TQueryKeyParams, TCacheData>(
  rootQueryKey: QueryKey
): QueryHelper<TQueryKeyParams, TCacheData> => {
  const helper: QueryHelper<TQueryKeyParams, TCacheData> = {
    getQueryData: (queryClient, queryKeyParams) =>
      queryClient.getQueryData(helper.makeQueryKey(queryKeyParams)),
    invalidateQuery: (queryClient, queryKeyParams) =>
      queryClient.invalidateQueries({
        queryKey: helper.makeQueryKey(queryKeyParams),
      }),
    invalidateRootQuery: (queryClient) =>
      queryClient.invalidateQueries({
        queryKey: helper.rootQueryKey(),
      }),
    makeQueryKey: (queryKeyParams) => [
      ...helper.rootQueryKey(),
      queryKeyParams,
    ],
    rootQueryKey: () => rootQueryKey,
    setQueryData: (queryClient, queryKeyParams, updaterFn) =>
      queryClient.setQueryData<TCacheData | undefined>(
        helper.makeQueryKey(queryKeyParams),
        (currentQueryData) =>
          currentQueryData === undefined
            ? currentQueryData
            : updaterFn(currentQueryData)
      ),
  }

  return helper
}
